import React, { useEffect, useState } from "react";
import { AppBar, Toolbar, Typography, IconButton, Box } from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import { styled } from "@mui/material/styles";
import "./MainMenu.scss";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../auth/authConfig";
import axios from "axios";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import UserGuideMenu from "../component/common/UserGuideMenu";
import { ShoppingCart } from "lucide-react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { CLEAR_CART } from "../../src/redux/domainAddToCart/domainAddToCart.type";
import CartSubscriptionModal from "../component/Layout/Domain/CartSubscriptionModal";
import { toast, Toaster } from "react-hot-toast";
import { useLocation } from "react-router-dom";

const drawerWidth = 240;

// Styled AppBar that adapts to the drawer state
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor: "white",
  borderBottom: "1px solid #E5E7EB",
  boxShadow: "none",
  marginLeft: open ? drawerWidth : 80, // <-- Here marginLeft is 240 if open, else 80
  width: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - 65px)`, // adjust width accordingly
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

const MainHeader = ({ open, handleDrawerOpen, text, text2 }) => {
  const { accounts, instance } = useMsal();
  const account = accounts?.[0];
  const name = account?.name || "User";
  const email = account?.username || "user@effem.com";
  const [photo, setPhoto] = useState(
    localStorage.getItem("profilePhoto") || null
  );
  const [anchorEl, setAnchorEl] = useState(null); // anchor element for menu
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [formTouched, setFormTouched] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [subscriptionForm, setSubscriptionForm] = useState({
    productName: "",
    projectCode: "",
    readerGroup: "",
    usageDuration: "3months",
    usageDescription: "",
    notificationEmail: "",
    acceptTerms: false,
    source: "DATAPULSE",
  });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [allFieldsValid, setAllFieldsValid] = useState(true);
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.domainAddToCart);
  const location = useLocation();

  const navigate = useNavigate();

  const getProfilePhoto = async () => {
    try {
      // Ensure you acquire a token for the signed-in user
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0], // Use the first account from the authenticated accounts
      });

      const token = response.accessToken;

      // Fetch the profile photo
      const photoResponse = await axios.get(
        "https://graph.microsoft.com/v1.0/me/photo/$value",
        {
          headers: {
            Authorization: `Bearer ${token}`, // Pass the token in the Authorization header
          },
          responseType: "arraybuffer", // To receive the image as binary data
        }
      );

      const blob = new Blob([photoResponse.data], { type: "image/jpeg" });
      const imageUrl = URL.createObjectURL(blob);
      setPhoto(imageUrl);
    } catch (err) {
      console.error("Error fetching profile photo", err);
    }
  };

  const handleSubscribe = () => {
    setShowSubscriptionForm(true);
  };

  const closeSubsciptionModal = () => {
    setFormTouched(false);
    setShowSubscriptionForm(false);
    setSubscriptionForm({
      productName: "",
      projectCode: "",
      readerGroup: "",
      usageDuration: "3months",
      usageDescription: "",
      notificationEmail: "",
      acceptTerms: false,
      source: "DATAPULSE",
    });
  };
  const handleSubscriptionChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    let _form = {
      ...subscriptionForm,
      [name]: newValue,
    };
    // populate reader group if exists
    if (name === "productName") {
      _form.readerGroup = e.target.readerGroup || "";
      _form.source = e.target.source || "DATAPULSE";
    }
    setSubscriptionForm(_form);

    // Real-time validation for email field
    if (name === "notificationEmail" && value) {
      const emailRegex = /^[^\s@]+@effem\.com$/;
      if (!emailRegex.test(value)) {
        setFormErrors((prev) => ({
          ...prev,
          notificationEmail: "Only @effem.com email addresses are allowed",
        }));
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.notificationEmail;
          return newErrors;
        });
      }
    }
  };

  const validateConfigForm = (formData = subscriptionForm) => {
    const errors = {};
    if (!formData.productName) errors.productName = "Required";
    if (!formData.readerGroup) errors.readerGroup = "Required";
    if (!formData.usageDuration) errors.usageDuration = "Required";
    if (!formData.usageDescription) errors.usageDescription = "Required";
    if (!formData.notificationEmail) {
      errors.notificationEmail = "Required";
    } else {
      // Email validation - only allow @effem.com domain
      const emailRegex = /^[^\s@]+@effem\.com$/;
      if (!emailRegex.test(formData.notificationEmail)) {
        errors.notificationEmail =
          "Only @effem.com email addresses are allowed";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubscriptionSubmit = async () => {
    setSubscriptionLoading(true);
    const trimmedForm = {
      ...subscriptionForm,
      productName: subscriptionForm.productName.trim(),
      projectCode: subscriptionForm.projectCode?.trim() || "",
      readerGroup: subscriptionForm.readerGroup.trim(),
      usageDescription: subscriptionForm.usageDescription.trim(),
      notificationEmail: subscriptionForm.notificationEmail.trim(),
      source: subscriptionForm.source?.toUpperCase() || "DATAPULSE",
    };

    setSubscriptionForm(trimmedForm);
    setFormTouched(true);
    // Now do full validation (including email format)
    if (!validateConfigForm(trimmedForm)) return;

    const requestDetails = {
      requestedBy: localStorage.getItem("userId"),
      usageDuration: trimmedForm.usageDuration,
      usageDescription: trimmedForm.usageDescription,
      readerGroup: trimmedForm.readerGroup,
      productName: trimmedForm.productName,
      projectCode: trimmedForm.projectCode || "",
      notificationEmail: trimmedForm.notificationEmail,
      source: trimmedForm.source,
    };

    const requestPayload = {
      dataAssetId: cartItems?.map((item) => item.id) || [],
      requestDetails: requestDetails,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/subscription-request`,
        requestPayload
      );

      if (response.status === 200 || response.status === 201) {
        closeSubsciptionModal();
        dispatch({ type: CLEAR_CART });
        setSubscriptionLoading(false);
      }

      toast.success("Subscription to the asset configured successfully!");
    } catch (error) {
      toast.error("Failed to subscribe to the asset. Please try again later.");
      setSubscriptionLoading(false);
    }
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    getProfilePhoto();
  }, []);
  return (
    <>
      <StyledAppBar
        sx={{ justifyContent: "center" }}
        position="fixed"
        open={open}
      >
        <Toolbar sx={{ gap: { xs: 1, sm: 2 }, overflow: "hidden", minHeight: { xs: "56px", sm: "64px" } }}>
          {/* Hamburger menu only shown when drawer is closed */}
          {!open && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
            >
              <MenuIcon sx={{ color: "#bb1ccc" }} />
            </IconButton>
          )}

          {/* Title */}
          <Typography
            className="menuHeader"
            sx={{
              color: "white",
              fontSize: { xs: "11px", sm: "14px", md: "20px" },
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: { xs: "2px", sm: "8px", md: "10px" },
              minWidth: 0,
              overflow: "hidden",
            }}
            variant="h6"
            noWrap
            component="div"
          >
            <span style={{ color: "#bb1ccc", overflow: "hidden", textOverflow: "ellipsis" }}> {text}</span>
            <span>
              {" "}
              {text2 && (
                <ArrowForwardIosIcon
                  sx={{ color: "#C1C1C1", fontSize: { xs: "12px", sm: "20px", md: "30px" } }}
                />
              )}
            </span>
            {text2 && (
              <Typography
                sx={{
                  color: "#bb1ccc",
                  fontSize: { xs: "11px", sm: "14px", md: "20px" },
                  fontWeight: "600",
                  fontFamily: "'Poppins', sans-serif",
                  display: { xs: "none", sm: "block" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                variant="h6"
                noWrap
                component="div"
              >
                {text2}
              </Typography>
            )}
          </Typography>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Icons section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1, md: 1 },
              alignContent: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {(location.pathname === "/dataDomain" || location.pathname === "/data-contract" || location.pathname === "/Dashboard"  || location.pathname === "/data-traffic-lights" || location.pathname === "/data-monitoring" || location.pathname === "/action"  || location.pathname === "/feature-requests") && cartItems && cartItems?.length > 0 && (
              <button
                onClick={handleSubscribe}
                className="bg-[#E31837] text-white text-[9px] sm:text-[12px] h-[32px] rounded-md px-2 sm:px-4 py-1 flex items-center gap-0.5 whitespace-nowrap"
              >
                <ShoppingCart className="h-4 sm:h-[20px]" />
                <span className="hidden sm:inline sm:text-sm">checkout ({cartItems?.length || 0})</span>
                <span className="sm:hidden text-[8px]">({cartItems?.length || 0})</span>
              </button>
            )}
            <UserGuideMenu />

            <div
              style={{
                height: "20px",
                borderLeft: "1px solid #bb1ccc",
                borderRight: "1px solid #bb1ccc",
                paddingLeft: "12px",
                paddingRight: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Tooltip
                title="Opens the production Feature Requests with a redirect notice"
                placement="bottom"
              >
                <Typography
                  role="button"
                  onClick={() => navigate("/feature-requests")}
                  sx={{
                    color: "#bb1ccc",
                    fontSize: { xs: "0px", sm: "14px" },
                    fontWeight: "400",
                    textDecoration: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <AssignmentTurnedInIcon
                    sx={{ fontSize: { xs: "20px", sm: "20px" }, color: "#bb1ccc" }}
                  />
                  <span className="hidden sm:inline">Leave feedback</span>
                </Typography>
              </Tooltip>
            </div>

            <div className="hidden sm:flex items-center gap-1 sm:gap-2 border-l border-white pl-2 sm:pl-4 h-[24px]">

              <Tooltip
                title={name || "User"}
                placement="bottom"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#bb1ccc",
                      color: "white",
                      fontSize: "0.85rem",
                      borderRadius: "6px",
                    },
                  },
                  arrow: { sx: { color: "black" } },
                }}
              >
                <span style={{ display: "inline-block", cursor: "pointer" }}>
                  <div className="flex h-[32px] sm:h-[30px] w-[32px] sm:w-[30px] items-center justify-center overflow-hidden rounded-full bg-gray-200 text-sm sm:text-lg font-semibold text-gray-700 select-none">
                    {photo ? (
                      <img
                        src={photo}
                        // alt={name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span>{name?.charAt(0).toUpperCase() || "?"}</span>
                    )}
                  </div>
                </span>
              </Tooltip>
            </div>
          </Box>
        </Toolbar>
      </StyledAppBar>

      <Toaster position="top-right" />

      {showSubscriptionForm && cartItems && cartItems?.length > 0 && (
        <CartSubscriptionModal
          subscriptionForm={subscriptionForm}
          closeSubsciptionModal={closeSubsciptionModal}
          handleSubscriptionSubmit={handleSubscriptionSubmit}
          handleSubscriptionChange={handleSubscriptionChange}
          setShowSubscriptionForm={setShowSubscriptionForm}
          showSubscriptionForm={showSubscriptionForm}
          allFieldsValid={allFieldsValid}
          formTouched={formTouched}
          formErrors={formErrors}
          loading={subscriptionLoading}
        />
      )}
    </>
  );
};

export default MainHeader;
